import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge as UiBadge } from "@/components/ui/badge";
import { ATTENDEE_TYPE_OPTIONS, CATEGORY_COLORS, TYPE_COLORS, resolveCategoryColor, type AttendeeType } from "./colors";
import type { Attendee } from "./types";

interface Props {
  attendees: Attendee[];
  regions: string[];
  selectedKeys: Set<string>;
  onSelect: (next: Set<string>) => void;
  onOverrideType: (key: string, type: AttendeeType) => void;
  onPreview: (key: string) => void;
}

export function AttendeesTable({ attendees, regions, selectedKeys, onSelect, onOverrideType, onPreview }: Props) {
  const [region, setRegion] = useState("all");
  const [type, setType] = useState("all");
  const [cat, setCat] = useState("all");
  const [regStatus, setRegStatus] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [search, setSearch] = useState("");

  const categories = useMemo(
    () => Array.from(new Set(attendees.map((a) => a.registrationCategory))).sort(),
    [attendees],
  );

  const filtered = useMemo(() => {
    return attendees.filter((a) => {
      if (region !== "all" && a.region !== region) return false;
      if (type !== "all" && a.attendeeType !== type) return false;
      if (cat !== "all" && a.registrationCategory !== cat) return false;
      if (regStatus !== "all" && a.registrationStatus !== regStatus) return false;
      if (paymentStatus !== "all" && a.paymentStatus !== paymentStatus) return false;
      if (search && !a.fullName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [attendees, region, type, cat, regStatus, paymentStatus, search]);

  const allChecked = filtered.length > 0 && filtered.every((a) => selectedKeys.has(a.key));
  const toggleAll = () => {
    const next = new Set(selectedKeys);
    if (allChecked) filtered.forEach((a) => next.delete(a.key));
    else filtered.forEach((a) => next.add(a.key));
    onSelect(next);
  };
  const toggleOne = (k: string) => {
    const next = new Set(selectedKeys);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onSelect(next);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <Label>Search name</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. John" />
        </div>
        <div>
          <Label>Region</Label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category</Label>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Attendee type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {ATTENDEE_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Reg status</Label>
          <Select value={regStatus} onValueChange={setRegStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Payment</Label>
          <Select value={paymentStatus} onValueChange={setPaymentStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-sm text-muted-foreground flex items-center gap-3">
        <span>{filtered.length} attendees · {selectedKeys.size} selected</span>
        <Button size="sm" variant="outline" onClick={() => onSelect(new Set(filtered.map((a) => a.key)))}>
          Select all filtered
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onSelect(new Set())}>Clear</Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-2 w-8"><Checkbox checked={allChecked} onCheckedChange={toggleAll} /></th>
              <th className="p-2">Name</th>
              <th className="p-2">Region</th>
              <th className="p-2">Category</th>
              <th className="p-2">Attendee type</th>
              <th className="p-2">Payment</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const color = TYPE_COLORS[a.attendeeType];
              const catColor = resolveCategoryColor(a.registrationCategory, a.attendeeType);
              return (
                <tr key={a.key} className="border-t hover:bg-muted/30">
                  <td className="p-2"><Checkbox checked={selectedKeys.has(a.key)} onCheckedChange={() => toggleOne(a.key)} /></td>
                  <td className="p-2 font-medium">{a.fullName || <span className="text-muted-foreground italic">Unnamed</span>}</td>
                  <td className="p-2">{a.region}</td>
                  <td className="p-2">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{ backgroundColor: `${catColor.bg}15`, color: catColor.bg, border: `1px solid ${catColor.bg}40` }}
                    >
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: catColor.bg }} />
                      {a.registrationCategory}
                    </span>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: color.bg }}
                      />
                      <Select value={a.attendeeType} onValueChange={(v) => onOverrideType(a.key, v as AttendeeType)}>
                        <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ATTENDEE_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                  <td className="p-2">
                    <UiBadge variant={a.paymentStatus === "confirmed" ? "default" : "secondary"}>
                      {a.paymentStatus}
                    </UiBadge>
                  </td>
                  <td className="p-2">
                    <Button size="sm" variant="outline" onClick={() => onPreview(a.key)}>Preview</Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No attendees match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
